import { createReducer, on } from '@ngrx/store';

import { RequestStatus } from '../../enums/request-status.enum';
import {
  ELink,
  ELinkDetail,
  ELinkManageOptions,
  ELinksListMeta,
  ELinksListParams,
} from '../models/e-link.model';
import * as ELinksActions from './e-links.actions';

export interface ELinksState {
  eLinks: ELink[];
  meta: ELinksListMeta | null;
  listStatus: RequestStatus;
  deleteStatus: RequestStatus;
  error: string | null;
  lastQuery: ELinksListParams;

  // Editor state
  currentELink: ELinkDetail | null;
  options: ELinkManageOptions | null;
  detailStatus: RequestStatus;
  optionsStatus: RequestStatus;
  saveStatus: RequestStatus;
  saveError: string | null;
  /** Field-level validation errors from a 422 save response. */
  saveFieldErrors: Record<string, string> | null;
}

const initialState: ELinksState = {
  eLinks: [],
  meta: null,
  listStatus: RequestStatus.Idle,
  deleteStatus: RequestStatus.Idle,
  error: null,
  lastQuery: {},

  currentELink: null,
  options: null,
  detailStatus: RequestStatus.Idle,
  optionsStatus: RequestStatus.Idle,
  saveStatus: RequestStatus.Idle,
  saveError: null,
  saveFieldErrors: null,
};

export const eLinksReducer = createReducer(
  initialState,
  on(ELinksActions.loadELinks, (state, query) => ({
    ...state,
    listStatus: RequestStatus.Loading,
    error: null,
    lastQuery: query,
  })),
  on(ELinksActions.loadELinksSuccess, (state, { eLinks, meta }) => ({
    ...state,
    eLinks,
    meta,
    listStatus: RequestStatus.Success,
  })),
  on(ELinksActions.loadELinksFailure, (state, { error }) => ({
    ...state,
    listStatus: RequestStatus.Error,
    error,
  })),
  on(ELinksActions.deleteELink, (state) => ({
    ...state,
    deleteStatus: RequestStatus.Loading,
    error: null,
  })),
  on(ELinksActions.deleteELinkSuccess, (state) => ({
    ...state,
    deleteStatus: RequestStatus.Success,
  })),
  on(ELinksActions.mutationFailure, (state, { error }) => ({
    ...state,
    deleteStatus:
      state.deleteStatus === RequestStatus.Loading ? RequestStatus.Error : state.deleteStatus,
    error,
  })),
  on(ELinksActions.resetELinksState, () => initialState),

  // Options
  on(ELinksActions.loadELinkOptions, (state) => ({
    ...state,
    optionsStatus: RequestStatus.Loading,
  })),
  on(ELinksActions.loadELinkOptionsSuccess, (state, { options }) => ({
    ...state,
    options,
    optionsStatus: RequestStatus.Success,
  })),
  on(ELinksActions.loadELinkOptionsFailure, (state, { error }) => ({
    ...state,
    optionsStatus: RequestStatus.Error,
    error,
  })),

  // Single load (detail / clone)
  on(ELinksActions.loadELink, (state) => ({
    ...state,
    currentELink: null,
    detailStatus: RequestStatus.Loading,
    error: null,
  })),
  on(ELinksActions.loadELinkSuccess, (state, { eLink }) => ({
    ...state,
    currentELink: eLink,
    detailStatus: RequestStatus.Success,
  })),
  on(ELinksActions.loadELinkFailure, (state, { error }) => ({
    ...state,
    detailStatus: RequestStatus.Error,
    error,
  })),

  // Save (create / update)
  on(ELinksActions.saveELink, (state) => ({
    ...state,
    saveStatus: RequestStatus.Loading,
    saveError: null,
    saveFieldErrors: null,
  })),
  on(ELinksActions.saveELinkSuccess, (state, { eLink }) => ({
    ...state,
    saveStatus: RequestStatus.Success,
    currentELink: eLink,
    saveFieldErrors: null,
  })),
  on(ELinksActions.saveELinkFailure, (state, { error, fieldErrors }) => ({
    ...state,
    saveStatus: RequestStatus.Error,
    saveError: error,
    saveFieldErrors: fieldErrors ?? null,
  })),

  on(ELinksActions.resetELinkEditor, (state) => ({
    ...state,
    currentELink: null,
    options: null,
    detailStatus: RequestStatus.Idle,
    optionsStatus: RequestStatus.Idle,
    saveStatus: RequestStatus.Idle,
    saveError: null,
    saveFieldErrors: null,
  }))
);
